// O que se OUVE numa ligação, fora a voz de quem fala: os dois catálogos de
// áudio que a organização configura.
//   - RINGBACK (/v1/ringback): o tom de "chamando" que o VENDEDOR escuta
//     enquanto o back origina a perna B. O back guarda só a ESCOLHA da org (uma
//     linha is_active); quem sintetiza o som é o front (sip/ringback-player).
//   - URA (/v1/ura): a biblioteca de áudios-template que tocam no início da
//     gravação — cadastro + upload para o S3, com URL de reprodução assinada
//     sob demanda.
//
// São duas rotas e duas features do back, mas um assunto só, e é por isso que
// dividem arquivo em vez de virarem dois módulos de meia centena de linhas:
// ambos são um ACERVO DE ÁUDIO da organização, cadastrado pelo gestor e tocado
// durante a chamada. Nenhum dos dois importa o outro, e nenhum importa
// ./sip.js: a telefonia (ramal, tronco, bina) é o caminho da ligação, isto aqui
// é o que trafega por ele.
import { api, apiUpload, toApiError } from "./client.js";

// --- Ringback (tom de "chamando") ---
// Feature separada (/v1/ringback). O back guarda a ESCOLHA da org (uma linha
// is_active); o áudio do catálogo é sintetizado no front (sip/ringback-player).

export type RingbackKey = "br-classic" | "us-classic" | "discreto" | "none";

export interface ActiveRingback {
  source: "catalog" | "upload";
  key: RingbackKey | null; // key do catálogo (null quando source=upload)
  audioUrl: string | null; // URL do upload (incremento 2)
}

export interface RingbackItem {
  id: string;
  name: string;
  source: "catalog" | "upload";
  catalogKey: string | null;
  audioKey: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RingbackConfig {
  items: RingbackItem[];
  active: ActiveRingback;
  viewerRole: string; // p/ a UI gatear o seletor de org (anti-botão-falso)
}

// GET /v1/ringback — tom ativo + linhas + papel de quem vê (qualquer membro).
export async function getRingbackConfig(): Promise<RingbackConfig> {
  const { data, error } = await api<RingbackConfig>("/v1/ringback");
  if (error) throw toApiError("GET /v1/ringback", error);
  return data;
}

// PUT /v1/ringback/active — ativa um tom do catálogo por key (gestor+).
export async function setActiveRingback(key: RingbackKey): Promise<ActiveRingback> {
  const { data, error } = await api<ActiveRingback>("/v1/ringback/active", {
    method: "PUT",
    body: { key },
  });
  if (error) throw toApiError("PUT /v1/ringback/active", error);
  return data;
}

// --- URAs (áudios-template que tocam no início da gravação) ---
// Biblioteca por organização. O áudio real vive no S3; a listagem traz só os
// metadados. Envio é multipart (gestor+); a URL de reprodução é assinada sob
// demanda (GET :id/audio), no padrão de getCallRecordingUrl.

export interface UraItem {
  id: string;
  organizationId: string;
  name: string;
  audioKey: string;
  mime: string | null;
  sizeBytes: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface UraListResponse {
  items: UraItem[];
  viewerRole: string;
}

// GET /v1/ura — URAs da org + papel de quem vê.
export async function getUras(): Promise<UraListResponse> {
  const { data, error } = await api<UraListResponse>("/v1/ura");
  if (error) throw toApiError("GET /v1/ura", error);
  return data;
}

// POST /v1/ura — envia um áudio de URA (multipart). Gestor+.
export async function uploadUra(input: { file: File; name?: string }): Promise<UraItem> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.name) form.append("name", input.name);
  return apiUpload<UraItem>("/v1/ura", form);
}

// GET /v1/ura/:id/audio — URL assinada para tocar o áudio.
export async function getUraAudioUrl(id: string): Promise<{ url: string }> {
  const { data, error } = await api<{ url: string }>(`/v1/ura/${id}/audio`);
  if (error) throw toApiError(`GET /v1/ura/${id}/audio`, error);
  return data;
}

// DELETE /v1/ura/:id — remove a URA (linha + objeto no S3). Gestor+.
export async function deleteUra(id: string): Promise<void> {
  const { error } = await api(`/v1/ura/${id}`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/ura/${id}`, error);
}
